import { User } from "../models/User";
import { MissedAcknowledgment} from "../models/MissedAcknowledgment";
import { Organization } from "../models/Organization";
import { Streak } from "../models/Streak";
import { filter, orderBy, groupBy, keys, forEach, find, omit, isEmpty, sortBy, uniq } from 'lodash';
import { ACKNOWLEDGEMENT_STATUS, OVERALL_HOS_INCIDENT_NAME } from '../constants';
import { hasVisibleHosIncident } from './incidentHelper';
import { isEqual, format, subWeeks } from 'date-fns';
import { AppMessages } from '../strings';

export interface IMissedAcknowledgmentReport {
	periods: IMissedAcknowledgmentPeriod[];
}

export interface IMissedAcknowledgmentPeriod {
	startDate?: Date,
	endDate?: Date,
	users: IMissedAcknowledgmentPeriodUser[]
	isCurrent: boolean
}

export interface IMissedAcknowledgmentPeriodUser {
	userID: number,
	firstName: string,
	lastName: string,
	acknowledgments: IMissedAcknowledgment[];
	acknowledgmentHistory: IMissedAcknowledgment[]
}

export interface IMissedAcknowledgment {
	status: ACKNOWLEDGEMENT_STATUS;
}

/** Driver who holds the top current or longest streak for a configured rule. */
export interface ITopStreakHolder {
	userID: number;
	firstName?: string;
	lastName?: string;
	days: number;
	miles?: number | null;
}

/** Top current and longest streak holders for one configured org streak rule. */
export interface IGroupTopStreakRule {
	streakID: number;
	name: string;
	longestHolder?: ITopStreakHolder | null;
	currentHolder?: ITopStreakHolder | null;
}

/** 5 historical weeks + 1 current week = 6 periods shown on the UI. */
export const MISSED_ACK_HISTORY_WEEKS = 5;

/**
 * Computes the date range sent to missingAcknowledgmentsReport.
 * startDate = startViewableWindow − MISSED_ACK_HISTORY_WEEKS, endDate = endViewableWindow.
 * Returns nulls when the org window is not loaded so the server applies its defaults.
 */
export function getMissingAcknowledgmentsQueryDateRange(organization?: Organization): { startDate: Date | null; endDate: Date | null } {
	if (!organization?.startViewableWindow || !organization?.endViewableWindow) {
		return { startDate: null, endDate: null };
	}
	return {
		startDate: subWeeks(new Date(organization.startViewableWindow), MISSED_ACK_HISTORY_WEEKS),
		endDate: new Date(organization.endViewableWindow),
	};
}

export function buildGroupDrivers (users?: User[]) : User[] {

	let dashboardUsers = [];

	dashboardUsers = filter(users, u => u.scorecard !== null && u.scorecard.currentScore !== null && u.scorecard.currentScore !== undefined && u.scorecard.currentScore < 90);

	return orderBy(dashboardUsers, user => user.scorecard.currentScore, ['asc']);
}

/**
 * Returns a display label for a configured streak rule.
 */
export function getConfiguredStreakLabel(streak: Streak): string {
	const name = streak.name?.trim();
	if (name) {
		return name;
	}

	const typeName = streak.streakType?.name?.trim();
	if (typeName) {
		return typeName;
	}

	return AppMessages.streakUntitled;
}

/**
 * Compares two candidate streak holders. Higher days win, then more scorecard miles, then lower userID.
 */
function compareTopStreakHolders(left: ITopStreakHolder, right: ITopStreakHolder): number {
	if (left.days !== right.days) {
		return left.days - right.days;
	}

	const leftMiles = left.miles ?? -1;
	const rightMiles = right.miles ?? -1;
	if (leftMiles !== rightMiles) {
		return leftMiles - rightMiles;
	}

	return right.userID - left.userID;
}

/**
 * Resolves configured streak rules from the organization payload.
 * Falls back to unique streak definitions found on group drivers when organization.streaks is empty.
 */
export function resolveConfiguredStreaks(configuredStreaks?: Streak[], users?: User[]): Streak[] {
	if (configuredStreaks?.length) {
		return configuredStreaks;
	}

	const streaksById: { [streakID: number]: Streak } = {};

	forEach(users, (driver) => {
		forEach(driver?.userStreaks, (userStreak) => {
			if (!userStreak?.streakID || streaksById[userStreak.streakID]) {
				return;
			}

			const nestedStreak = userStreak.streak;
			streaksById[userStreak.streakID] = {
				streakID: userStreak.streakID,
				name: nestedStreak?.name ?? "",
				description: nestedStreak?.description,
				streakType: nestedStreak?.streakType,
			};
		});
	});

	return Object.values(streaksById);
}

/**
 * Builds top longest and current streak holders for each configured organization streak rule.
 * Rules with no progress remain in the result with null holders.
 * When organization.streaks is missing/empty, rules are derived from driver userStreaks.
 */
export function buildGroupTopStreaks(configuredStreaks?: Streak[], users?: User[]): IGroupTopStreakRule[] {
	const drivers = users ?? [];
	const streakRules = resolveConfiguredStreaks(configuredStreaks, drivers);

	if (!streakRules.length) {
		return [];
	}

	return streakRules.map((configuredStreak) => {
		let longestHolder: ITopStreakHolder | null = null;
		let currentHolder: ITopStreakHolder | null = null;

		forEach(drivers, (driver) => {
			if (!driver?.userID) {
				return;
			}

			const matchingStreak = find(driver.userStreaks, (userStreak) => userStreak.streakID === configuredStreak.streakID);
			if (!matchingStreak) {
				return;
			}

			const miles = driver.scorecard?.currentTotalDistanceMiles ?? null;

			if (matchingStreak.longestStreakDays != null && matchingStreak.longestStreakDays > 0) {
				const candidate: ITopStreakHolder = {
					userID: driver.userID,
					firstName: driver.firstName,
					lastName: driver.lastName,
					days: matchingStreak.longestStreakDays,
					miles,
				};

				if (!longestHolder || compareTopStreakHolders(candidate, longestHolder) > 0) {
					longestHolder = candidate;
				}
			}

			if (matchingStreak.currentStreakDays != null && matchingStreak.currentStreakDays > 0) {
				const candidate: ITopStreakHolder = {
					userID: driver.userID,
					firstName: driver.firstName,
					lastName: driver.lastName,
					days: matchingStreak.currentStreakDays,
					miles,
				};

				if (!currentHolder || compareTopStreakHolders(candidate, currentHolder) > 0) {
					currentHolder = candidate;
				}
			}
		});

		return {
			streakID: configuredStreak.streakID,
			name: getConfiguredStreakLabel(configuredStreak),
			longestHolder,
			currentHolder,
		};
	});
}

export function buildGroupDriversWithIncident (users: User[], incidentName: string) : User[] {

	let result: User[] = [];

	forEach(users, user => {

		// For the Overall HOS rollup, only match drivers who have at least one HOS incident
		// severe enough for buildIncidents to actually surface a rollup panel (shared
		// predicate, see hasVisibleHosIncident); otherwise the carousel would land on a
		// driver with no matching panel.
		const hasIncident = incidentName === OVERALL_HOS_INCIDENT_NAME
			? hasVisibleHosIncident(user.scorecard.incidents)
			: !isEmpty(filter(user.scorecard.incidents, i => i.name === incidentName))

		if (hasIncident) {
			result.push(user);
		}
	})

	return result;
}

export function buildMissedAcknowledgments (organization?: Organization, data?: [MissedAcknowledgment]) : IMissedAcknowledgmentPeriod[] {

	var groupedData = groupBy(data, 'startDate');

	var resultKeys = keys(groupedData);

	const report : IMissedAcknowledgmentReport = { periods: [] }

	if (organization === undefined || data === undefined){
		return report.periods;
	}


	forEach(resultKeys, key => {

	  const val = groupedData[key];
	  
	  var period = find(report.periods, period => period.startDate === key);

	  if (period === undefined) {
	  	period = {startDate: new Date(key), endDate: new Date(val.endDate), users: [], isCurrent: false}
	  }

	  forEach(val, ack => {
	    
	    const user : IMissedAcknowledgmentPeriodUser = {userID: ack.user?.userID, firstName: ack.user?.firstName, lastName: ack.user?.lastName, acknowledgments: [], acknowledgmentHistory: []}
	    period.endDate = new Date(ack.endDate);
	    var existingUser = find(period.users, u => u.userID === user.userID)

	    if (existingUser === undefined) {

	    	period.users.push(user);
	    }
	    	    
	    user.acknowledgments.push(omit(ack, 'user'))
	  })

	  report.periods.push(period);
	})


	// Check if it has a period that matches the org date
	if (organization?.startViewableWindow) {
		const organizationStartDate = new Date(organization.startViewableWindow); 

		const existingPeriod = find(report.periods, p => isEqual(p.startDate, organizationStartDate));

		if (!existingPeriod) {

			const currentPeriodPlaceholder = {startDate: organization?.startViewableWindow ? organizationStartDate : undefined, endDate: organization?.endViewableWindow ? new Date(organization?.endViewableWindow) : undefined, users: [], isCurrent: true }
			report.periods.push(currentPeriodPlaceholder);
		} else {
			existingPeriod.isCurrent = true;
		}	
	}


	report.periods = orderBy(report.periods, 'startDate', ['desc']);

	if (!isEmpty(report.periods)) {

		// Temporary until we sort out the timezone
		// report.periods[0].isCurrent = true;

		const currentPeriod = find(report.periods, p => p.isCurrent === true);
		if (!currentPeriod) {
			return report.periods;
		}

		currentPeriod.users = filter(currentPeriod.users, u => filter(u.acknowledgments, ack => ack.acknowledged === ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED).length > 0)
		currentPeriod.users = sortBy(currentPeriod.users, ['firstName', 'lastName']);

		const remainingPeriods = filter(report.periods, p => p !== currentPeriod);

		forEach(currentPeriod.users, u => {

			const ack = find(u.acknowledgments, a => isEqual(new Date(a.startDate), currentPeriod.startDate));

			if (ack) {

				const historicalAckReport : IMissedAcknowledgment = {status: ack.acknowledged}

				u.acknowledgmentHistory.push(historicalAckReport);
			}

			forEach(remainingPeriods, period => {


				const historicalUserReport = find(period.users, periodUser => periodUser.userID === u.userID);

				if (historicalUserReport) {

					const userHistoricalReportAck = find(historicalUserReport.acknowledgments, ack => isEqual(new Date(ack.startDate), period.startDate));

					if (userHistoricalReportAck) {
						const historicalAckReport : IMissedAcknowledgment = {status: userHistoricalReportAck.acknowledged}
						u.acknowledgmentHistory.push(historicalAckReport);
					} else {
						console.error("Did not find historical ack for user id " + u.userID + " and start date ", period.startDate);
						u.acknowledgmentHistory.push({ status: ACKNOWLEDGEMENT_STATUS.NO_DATA });
					}
				} else {
					const historicalAckReport : IMissedAcknowledgment = {status: ACKNOWLEDGEMENT_STATUS.NO_DATA}
					u.acknowledgmentHistory.push(historicalAckReport);
				}
			})
		})


		forEach(remainingPeriods, period => {
			period.users = filter(period.users, u => filter(u.acknowledgments, ack => ack.acknowledged === ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED).length > 0)
			period.users = sortBy(period.users, ['firstName', 'lastName']);
		})

	}

	return report.periods;
}

/** Raw item from missingAcknowledgmentsReport API used for CSV export */
export interface IMissingAcknowledgmentsReportItem {
	acknowledgmentID: string;
	acknowledged: string;
	acknowledgedDate?: string | null;
	startDate: string;
	endDate?: string;
	locationGroupID: number;
	locationGroupName?: string;
	user?: {
		userID: number;
		firstName: string;
		lastName: string;
	};
}

function escapeCsvCell(value: string): string {
	if (value == null) return '';
	const s = String(value);
	if (s.includes(',') || s.includes('"') || s.includes('\n')) {
		return '"' + s.replace(/"/g, '""') + '"';
	}
	return s;
}

/**
 * Build CSV content and filename for Missed Acknowledgements report.
 * Only includes drivers who missed at least one acknowledgement across the 6-week window.
 * The API returns all acks (both ACKNOWLEDGED and NOT_ACKNOWLEDGED) so each week column
 * correctly reflects Yes/No even when the driver acknowledged some weeks and missed others.
 */
export function buildMissedAcknowledgmentsCsv(
	reportData: IMissingAcknowledgmentsReportItem[] | undefined,
	scorecardStartDate: Date | string | undefined,
	scorecardEndDate: Date | string | undefined,
	organizationName?: string
): { csv: string; filename: string } {
	const now = format(new Date(), 'MMddyy_HHmm');
	const orgLabel = organizationName ?? 'Report';

	if (!reportData || reportData.length === 0) {
		const csv = 'Lastname,Firstname,Location Group';
		const startLabel = scorecardStartDate ? format(new Date(scorecardStartDate), 'MMddyy') : 'na';
		const endLabel = scorecardEndDate ? format(new Date(scorecardEndDate), 'MMddyy') : 'na';
		const filename = `Acknowledgments_${orgLabel}_${startLabel}_to_${endLabel}_on_${now}.csv`;
		return { csv, filename };
	}

	const csvLines: string[] = [];

	// Unique week start dates, most recent to least (desc)
	const weekStarts = uniq(reportData.map((a) => a.startDate)).sort(
		(a, b) => new Date(b).getTime() - new Date(a).getTime()
	);

	// Group by (userID, locationGroupName)
	const key = (a: IMissingAcknowledgmentsReportItem) =>
		`${a.user?.userID ?? -1}|${a.locationGroupName ?? a.locationGroupID ?? ''}`;
	const byDriverLocation = groupBy(reportData, key);

	// Only drivers who missed at least one
	const driverKeys = keys(byDriverLocation).filter((k) =>
		(byDriverLocation[k] ?? []).some((a) => a.acknowledged === ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED)
	);

	// Build row per (user, locationGroup): lastName, firstName, locationGroup, then Yes/No per week
	const rowData = driverKeys.map((k) => {
		const acks = byDriverLocation[k] ?? [];
		const firstAck = acks[0];
		const user = firstAck?.user;
		const locationGroupName = firstAck?.locationGroupName ?? String(firstAck?.locationGroupID ?? '');
		const ackByStart = groupBy(acks, 'startDate');
		const weekValues = weekStarts.map((start) => {
			const ack = (ackByStart[start] ?? [])[0];
			if (!ack) return 'N/A';
			if (ack.acknowledged === ACKNOWLEDGEMENT_STATUS.ACK_NOT_REQUIRED) return '--';
			return ack.acknowledged === ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED ? 'Yes' : 'No';
		});
		return {
			lastName: user?.lastName ?? '',
			firstName: user?.firstName ?? '',
			locationGroupName,
			weekValues
		};
	});

	// Sort by lastname, firstname, location group
	const sorted = sortBy(rowData, ['lastName', 'firstName', 'locationGroupName']);

	// Header: Lastname, Firstname, location group, then 6 week start dates
	const weekHeaders = weekStarts.map((d) => format(new Date(d), 'MM/dd/yy'));
	const headerRow = ['Lastname', 'Firstname', 'Location Group', ...weekHeaders].map(escapeCsvCell).join(',');
	csvLines.push(headerRow);

	sorted.forEach((row) => {
		const rowCells = [
			row.lastName,
			row.firstName,
			row.locationGroupName,
			...row.weekValues
		].map(escapeCsvCell);
		csvLines.push(rowCells.join(','));
	});

	const csv = csvLines.join('\r\n');
	const startLabel =
		weekStarts.length > 0 ? format(new Date(weekStarts[weekStarts.length - 1]), 'MMddyy') : scorecardStartDate ? format(new Date(scorecardStartDate), 'MMddyy') : 'na';
	const endLabel =
		weekStarts.length > 0 ? format(new Date(weekStarts[0]), 'MMddyy') : scorecardEndDate ? format(new Date(scorecardEndDate), 'MMddyy') : 'na';
	const filename = `Acknowledgments_${orgLabel}_${startLabel}_to_${endLabel}_on_${now}.csv`;

	return { csv, filename };
}

/*
function loadTestData() : IMissedAcknowledgmentPeriod[] {
	const result : IMissedAcknowledgmentPeriod[] = [{
			"startDate": new Date("2023-08-20T06:00:00.000Z"),
			"endDate": new Date("2023-08-27T05:59:59.990Z"),
			"users": [{
				"userID": 23760,
				"firstName": "David",
				"lastName": "Owens",
				"acknowledgments": [],
				"acknowledgmentHistory": [{status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACK_NOT_REQUIRED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}]
			}, {
				"userID": 20354,
				"firstName": "Todd",
				"lastName": "Taylor",
				"acknowledgments": [],
				"acknowledgmentHistory": [{status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}]
			}, {
				"userID": 15365,
				"firstName": "Christopher",
				"lastName": "Waddell",
				"acknowledgments": [],
				"acknowledgmentHistory": [{status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACK_NOT_REQUIRED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}]
			}, {
				"userID": 23058,
				"firstName": "William",
				"lastName": "Dickey",
				"acknowledgments": [],
				"acknowledgmentHistory": [{status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACK_NOT_REQUIRED}]
			}, {
				"userID": 15879,
				"firstName": "Robert",
				"lastName": "Frost",
				"acknowledgments": [],
				"acknowledgmentHistory": [{status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.NOT_ACKNOWLEDGED}, {status: ACKNOWLEDGEMENT_STATUS.ACKNOWLEDGED}]
			}],
			"isCurrent": true
		}, {
			"startDate": new Date("2023-08-13T06:00:00.000Z"),
			"endDate": new Date("2023-08-20T05:59:59.990Z"),
			"users": [{
			"userID": 23760,
			"firstName": "David",
			"lastName": "Owens",
			"acknowledgments": [],
			"acknowledgmentHistory": []
			}],
			"isCurrent": false
		}, {
			"startDate": new Date("2023-08-06T06:00:00.000Z"),
			"endDate": new Date("2023-08-13T05:59:59.990Z"),
			"users": [],
			"isCurrent": false
		}, {
			"startDate": new Date("2023-07-30T06:00:00.000Z"),
			"endDate": new Date("2023-08-06T05:59:59.990Z"),
			"users": [],
			"isCurrent": false
		}, {
			"startDate": new Date("2023-07-23T06:00:00.000Z"),
			"endDate": new Date("2023-07-30T05:59:59.990Z"),
			"users": [],
			"isCurrent": false
		}, {
			"startDate": new Date("2023-07-16T06:00:00.000Z"),
			"endDate": new Date("2023-07-23T05:59:59.990Z"),
			"users": [],
			"isCurrent": false
		}]

	return result;
}
*/
